export type FaqBlockType = 'paragraph' | 'heading' | 'list' | 'image' | 'link';

export interface FaqAnswerBlock {
  type: FaqBlockType;
  text?: string;
  style?: 'ordered' | 'unordered';
  items?: string[];
  url?: string;
  alt?: string;
  caption?: string;
}

export interface FaqAnswer {
  version: 1;
  blocks: FaqAnswerBlock[];
}

export interface ImgCloudFaq {
  id: number;
  category: string;
  question: string;
  answer: FaqAnswer;
  sortOrder: number;
  updatedAt?: string;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

export const FAQ_BOOTSTRAP_ELEMENT_ID = 'img-cloud-faq-data';

const isFaqArray = (value: unknown): value is ImgCloudFaq[] =>
  Array.isArray(value) && value.every((item) => {
    if (!item || typeof item !== 'object') return false;
    const faq = item as Partial<ImgCloudFaq>;
    return (
      typeof faq.id === 'number' &&
      typeof faq.category === 'string' &&
      typeof faq.question === 'string' &&
      faq.answer?.version === 1 &&
      Array.isArray(faq.answer.blocks)
    );
  });

export const readFaqBootstrapData = (): ImgCloudFaq[] => {
  const element = document.getElementById(FAQ_BOOTSTRAP_ELEMENT_ID);
  if (!element?.textContent) return [];
  try {
    const value: unknown = JSON.parse(element.textContent);
    return isFaqArray(value) ? value : [];
  } catch (error) {
    console.warn('Failed to parse FAQ build data', error);
    return [];
  }
};

export const fetchPublishedFaqs = async (): Promise<ImgCloudFaq[]> => {
  const response = await fetch('/api/img-cloud/faqs', {
    method: 'GET',
    cache: 'no-store',
    credentials: 'same-origin',
  });
  const result = (await response.json()) as ApiResponse<unknown>;
  if (!response.ok || !result.success || !isFaqArray(result.data)) {
    throw new Error(result.message || '常见问题加载失败');
  }
  return result.data;
};

export const faqAnswerText = (faq: ImgCloudFaq): string =>
  faq.answer.blocks
    .flatMap((block) => [block.text, block.alt, block.caption, ...(block.items || [])])
    .filter((value): value is string => !!value)
    .join(' ');
